import React, { useEffect } from 'react';
import { Cloud, Server, TrendingUp } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
//  ANIMATION STYLES (injected once)
// ═══════════════════════════════════════════════════════════════════

const STYLES = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-6px); }
  }
  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.9; }
  }
  @keyframes upload-dash {
    0% { stroke-dashoffset: 30; opacity: 0; }
    20% { opacity: 1; }
    100% { stroke-dashoffset: -30; opacity: 0; }
  }
  @keyframes arrow-bob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  @keyframes particle-drift {
    0% { transform: translate(0, 0) scale(1); opacity: 0; }
    20% { opacity: 1; }
    100% { transform: translate(var(--dx, 8px), var(--dy, -12px)) scale(0.4); opacity: 0; }
  }
  @keyframes rack-blink {
    0%, 90%, 100% { opacity: 0.9; }
    95% { opacity: 0.3; }
  }
  @keyframes rack-activity {
    0%, 100% { width: 8px; }
    50% { width: 22px; }
  }
  @keyframes shield-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.04); opacity: 0.85; }
  }
  @keyframes disk-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes nas-led-blink {
    0%, 80%, 100% { opacity: 0.9; }
    90% { opacity: 0.2; }
  }
  @keyframes wifi-ping {
    0% { opacity: 0; transform: scale(0.7); }
    40% { opacity: 0.7; }
    100% { opacity: 0; transform: scale(1.15); }
  }
  @keyframes warning-flash {
    0%, 100% { stroke: #ef4444; }
    50% { stroke: #f97316; }
  }
  @keyframes checkmark-draw {
    0% { stroke-dashoffset: 30; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes data-pulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 0.8; transform: scale(1.2); }
  }
  @keyframes slide-up {
    from { transform: translateY(24px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-float { animation: float 4s ease-in-out infinite; }
  .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
  .animate-slide-up { animation: slide-up 0.6s cubic-bezier(0.22,1,0.36,1) both; }
  .animate-fade-in { animation: fade-in 0.5s ease both; }
`;

function injectStyles() {
    if (document.getElementById('ct-anim-styles')) return;
    const s = document.createElement('style');
    s.id = 'ct-anim-styles';
    s.textContent = STYLES;
    document.head.appendChild(s);
}

// ═══════════════════════════════════════════════════════════════════
//  CLOUD ILLUSTRATIONS (animated)
// ═══════════════════════════════════════════════════════════════════

const CloudIllustration1 = () => (
    <svg viewBox="0 0 160 130" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
            <radialGradient id="ci1_glow" cx="50%" cy="60%" r="50%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="ci1_cloud" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
        </defs>

        {/* Floating cloud body */}
        <g style={{ animation: 'float 5s ease-in-out infinite' }}>
            <ellipse cx="80" cy="108" rx="52" ry="7" fill="url(#ci1_glow)" />
            <path
                d="M112 78 C116 78 122 74 122 66 C122 58 115 52 106 54 C103 45 95 38 83 38 C68 38 56 49 55 62 C49 62 40 67 40 76 C40 85 48 90 57 90 L108 90 C110 90 112 89 112 78Z"
                fill="url(#ci1_cloud)" stroke="#475569" strokeWidth="1.2"
            />
            <path d="M62 57 C65 50 73 45 83 45" stroke="#64748b" strokeWidth="1.3" strokeLinecap="round" />
        </g>

        {/* Animated upload dashes */}
        <line
            x1="80" y1="108" x2="80" y2="72"
            stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"
            strokeDasharray="5 4"
            style={{ animation: 'upload-dash 1.8s linear infinite' }}
        />

        {/* Arrow head bob */}
        <g style={{ animation: 'arrow-bob 1.8s ease-in-out infinite' }}>
            <path d="M72 78 L80 69 L88 78" fill="none" stroke="#60a5fa" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* Floating particles */}
        <circle cx="42" cy="42" r="2.5" fill="#3b82f6"
            style={{ animation: 'particle-drift 3s ease-out infinite', '--dx': '-6px', '--dy': '-10px' } as React.CSSProperties} />
        <circle cx="122" cy="48" r="1.8" fill="#818cf8"
            style={{ animation: 'particle-drift 3.5s ease-out 0.8s infinite', '--dx': '8px', '--dy': '-8px' } as React.CSSProperties} />
        <circle cx="50" cy="108" r="1.8" fill="#60a5fa"
            style={{ animation: 'particle-drift 4s ease-out 1.4s infinite', '--dx': '-4px', '--dy': '-14px' } as React.CSSProperties} />
        <circle cx="115" cy="103" r="2.5" fill="#94a3b8"
            style={{ animation: 'particle-drift 3.2s ease-out 0.3s infinite', '--dx': '6px', '--dy': '-10px' } as React.CSSProperties} />
        <circle cx="130" cy="72" r="1.5" fill="#6366f1"
            style={{ animation: 'particle-drift 2.8s ease-out 1.1s infinite', '--dx': '5px', '--dy': '-12px' } as React.CSSProperties} />
    </svg>
);

const CloudIllustration2 = () => (
    <svg viewBox="0 0 160 130" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
            <linearGradient id="ci2_rack" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#0f172a" stopOpacity="0.9" />
            </linearGradient>
        </defs>
        <rect x="38" y="22" width="76" height="84" rx="6" fill="url(#ci2_rack)" stroke="#1e40af" strokeWidth="1.3" />
        <rect x="32" y="28" width="8" height="72" rx="3" fill="#0f172a" stroke="#1e3a5f" strokeWidth="1" />
        <rect x="112" y="28" width="8" height="72" rx="3" fill="#0f172a" stroke="#1e3a5f" strokeWidth="1" />

        {[32, 48, 64, 80].map((y, i) => (
            <g key={i}>
                <rect x="46" y={y} width="60" height="12" rx="2" fill="#0a1628" stroke="#1e40af" strokeWidth="0.8" />
                <rect x="50" y={y + 3} width="6" height="6" rx="1" fill="#1e3a5f" stroke="#2563eb" strokeWidth="0.5" />
                <rect x="58" y={y + 3} width="6" height="6" rx="1" fill="#1e3a5f" stroke="#2563eb" strokeWidth="0.5" />
                {/* Animated LED */}
                <circle cx="96" cy={y + 6} r="2" fill={i === 1 ? '#f59e0b' : '#22c55e'}
                    style={{ animation: `rack-blink ${1.5 + i * 0.4}s ease-in-out ${i * 0.3}s infinite` }} />
                <rect x="66" y={y + 4} width="22" height="4" rx="1" fill="#0f2040" />
                {/* Animated activity bar */}
                <rect x="66" y={y + 4} height="4" rx="1" fill={i === 1 ? '#f59e0b' : '#3b82f6'} opacity="0.7"
                    style={{ animation: `rack-activity ${2 + i * 0.5}s ease-in-out ${i * 0.2}s infinite`, width: `${8 + i * 4}px` }} />
            </g>
        ))}

        <path d="M114 35 C128 35 128 55 114 55" stroke="#1e40af" strokeWidth="0.9" strokeDasharray="2 2" fill="none" />
        <path d="M114 68 C132 68 132 88 114 88" stroke="#1d4ed8" strokeWidth="0.9" strokeDasharray="2 2" fill="none" />

        {/* Animated warning badge */}
        <g style={{ animation: 'float 3s ease-in-out infinite' }}>
            <circle cx="118" cy="20" r="9" fill="#1e293b" stroke="#ef4444" strokeWidth="1.2"
                style={{ animation: 'warning-flash 2s ease-in-out infinite' }} />
            <path d="M118 14 L122 23 H114 Z" fill="none" stroke="#ef4444" strokeWidth="1.2" strokeLinejoin="round"
                style={{ animation: 'warning-flash 2s ease-in-out infinite' }} />
            <line x1="118" y1="17" x2="118" y2="20" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="118" cy="22" r="0.8" fill="#ef4444" />
        </g>

        <rect x="70" y="110" width="12" height="9" rx="2" fill="#1e293b" stroke="#475569" strokeWidth="1" />
        <path d="M72 110 V107 C72 103 82 103 82 107 V110" fill="none" stroke="#475569" strokeWidth="1" />
        <circle cx="76" cy="115" r="1.5" fill="#64748b" />
    </svg>
);

// ═══════════════════════════════════════════════════════════════════
//  NAS ILLUSTRATIONS (animated)
// ═══════════════════════════════════════════════════════════════════

const NasIllustration1 = () => (
    <svg viewBox="0 0 160 130" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
            <linearGradient id="ni1_body" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#312e81" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#1e1b4b" stopOpacity="0.95" />
            </linearGradient>
            <linearGradient id="ni1_face" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1e1b4b" />
                <stop offset="100%" stopColor="#0f0e2a" />
            </linearGradient>
            <radialGradient id="ni1_glow" cx="50%" cy="80%" r="50%">
                <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
            </radialGradient>
        </defs>

        <ellipse cx="80" cy="112" rx="45" ry="6" fill="url(#ni1_glow)"
            style={{ animation: 'pulse-glow 3s ease-in-out infinite' }} />

        {/* Floating NAS body */}
        <g style={{ animation: 'float 5s ease-in-out infinite' }}>
            <rect x="42" y="22" width="76" height="88" rx="8" fill="url(#ni1_body)" stroke="#4c1d95" strokeWidth="1.2" />
            <rect x="50" y="28" width="52" height="76" rx="5" fill="url(#ni1_face)" stroke="#3730a3" strokeWidth="0.8" />
            <rect x="54" y="32" width="44" height="8" rx="2" fill="#1e1b4b" />
            <text x="76" y="39" textAnchor="middle" fill="#818cf8" fontSize="5" fontWeight="bold" letterSpacing="0.5">SYNOLOGY</text>

            {/* Drive bay 1 */}
            <rect x="54" y="46" width="44" height="24" rx="3" fill="#12103a" stroke="#3730a3" strokeWidth="0.8" />
            <rect x="57" y="49" width="38" height="18" rx="2" fill="#0a0920" stroke="#2e2a7a" strokeWidth="0.6" />
            <rect x="59" y="53" width="20" height="3" rx="1" fill="#312e81" />
            <rect x="59" y="53" width="14" height="3" rx="1" fill="#6d28d9" opacity="0.9"
                style={{ animation: 'rack-activity 2.3s ease-in-out infinite' }} />
            <circle cx="89" cy="57" r="2.2" fill="#a78bfa"
                style={{ animation: 'nas-led-blink 2.1s ease-in-out infinite' }} />

            {/* Drive bay 2 */}
            <rect x="54" y="74" width="44" height="24" rx="3" fill="#12103a" stroke="#3730a3" strokeWidth="0.8" />
            <rect x="57" y="77" width="38" height="18" rx="2" fill="#0a0920" stroke="#2e2a7a" strokeWidth="0.6" />
            <rect x="59" y="81" width="20" height="3" rx="1" fill="#312e81" />
            <rect x="59" y="81" width="18" height="3" rx="1" fill="#7c3aed" opacity="0.9"
                style={{ animation: 'rack-activity 1.9s ease-in-out 0.4s infinite' }} />
            <circle cx="89" cy="85" r="2.2" fill="#22c55e"
                style={{ animation: 'nas-led-blink 3s ease-in-out 0.7s infinite' }} />

            {/* Power button */}
            <circle cx="76" cy="104" r="4" fill="#1e1b4b" stroke="#4c1d95" strokeWidth="0.8" />
            <path d="M76 101 V104" stroke="#a78bfa" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M73.5 102 A4 4 0 1 0 78.5 102" fill="none" stroke="#a78bfa" strokeWidth="1.2" strokeLinecap="round" />
            <rect x="86" y="102" width="6" height="4" rx="1" fill="#0f0e2a" stroke="#3730a3" strokeWidth="0.6" />
            {[35, 42, 49, 56, 63].map((y) => (
                <line key={y} x1="42" y1={y} x2="46" y2={y} stroke="#4c1d95" strokeWidth="0.8" strokeLinecap="round" />
            ))}
        </g>

        {/* Animated wifi arcs */}
        <path d="M122 62 C126 57 134 57 138 62" fill="none" stroke="#7c3aed" strokeWidth="1.2" strokeLinecap="round"
            style={{ animation: 'wifi-ping 2.2s ease-out infinite' }} />
        <path d="M125 67 C128 63 134 63 137 67" fill="none" stroke="#a78bfa" strokeWidth="1.2" strokeLinecap="round"
            style={{ animation: 'wifi-ping 2.2s ease-out 0.5s infinite' }} />

        {/* Drifting data particles */}
        <circle cx="130" cy="55" r="2" fill="#7c3aed"
            style={{ animation: 'data-pulse 2s ease-in-out infinite' }} />
        <circle cx="138" cy="65" r="1.4" fill="#a78bfa"
            style={{ animation: 'data-pulse 2.6s ease-in-out 0.4s infinite' }} />
        <circle cx="133" cy="78" r="1.8" fill="#6d28d9"
            style={{ animation: 'data-pulse 2.1s ease-in-out 0.9s infinite' }} />
    </svg>
);

const NasIllustration2 = () => (
    <svg viewBox="0 0 160 130" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
            <radialGradient id="ni2_glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="ni2_shield" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#312e81" />
                <stop offset="100%" stopColor="#1e1b4b" />
            </linearGradient>
            <linearGradient id="ni2_disk" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
        </defs>

        <ellipse cx="80" cy="70" rx="55" ry="50" fill="url(#ni2_glow)"
            style={{ animation: 'pulse-glow 3s ease-in-out infinite' }} />

        {/* Spinning disk platter */}
        <g style={{ transformOrigin: '80px 92px', animation: 'disk-spin 8s linear infinite' }}>
            <ellipse cx="80" cy="92" rx="36" ry="10" fill="url(#ni2_disk)" stroke="#334155" strokeWidth="1.2" />
            <ellipse cx="80" cy="92" rx="26" ry="7" fill="#0a0920" stroke="#1e293b" strokeWidth="0.8" />
            <ellipse cx="80" cy="92" rx="16" ry="4.5" fill="none" stroke="#1e3a5f" strokeWidth="0.6" strokeDasharray="3 2" />
            <ellipse cx="80" cy="92" rx="22" ry="6.5" fill="none" stroke="#1e3a5f" strokeWidth="0.5" strokeDasharray="4 3" />
        </g>
        <ellipse cx="80" cy="92" rx="6" ry="2" fill="#1e293b" stroke="#475569" strokeWidth="0.6" />

        {/* Read arm */}
        <line x1="80" y1="92" x2="104" y2="78" stroke="#475569" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="104" cy="78" r="2.5" fill="#334155" stroke="#64748b" strokeWidth="0.8" />

        {/* Floating shield with pulse */}
        <g style={{ animation: 'float 4s ease-in-out infinite' }}>
            <path
                d="M80 16 L100 24 V46 C100 58 90 66 80 70 C70 66 60 58 60 46 V24 Z"
                fill="url(#ni2_shield)" stroke="#6d28d9" strokeWidth="1.4"
                style={{ animation: 'shield-pulse 3s ease-in-out infinite' }}
            />
            <path d="M80 21 L96 28 V46 C96 56 87 63 80 67 C73 63 64 56 64 46 V28 Z"
                fill="none" stroke="#7c3aed" strokeWidth="0.7" opacity="0.5" />
            {/* Animated checkmark draw */}
            <path d="M71 44 L77 50 L89 36" fill="none" stroke="#a78bfa" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"
                strokeDasharray="30" strokeDashoffset="0"
                style={{ animation: 'checkmark-draw 2s ease-in-out 0.5s infinite alternate' }} />
        </g>

        <line x1="80" y1="70" x2="80" y2="83" stroke="#6d28d9" strokeWidth="1" strokeDasharray="3 2" strokeLinecap="round" />

        {/* Floating ambient particles */}
        {[
            { cx: 118, cy: 40, r: 2, color: '#7c3aed', delay: '0s', dur: '2.5s' },
            { cx: 125, cy: 55, r: 1.5, color: '#a78bfa', delay: '0.7s', dur: '3s' },
            { cx: 40, cy: 50, r: 2, color: '#6d28d9', delay: '0.3s', dur: '2.8s' },
            { cx: 34, cy: 66, r: 1.5, color: '#7c3aed', delay: '1.1s', dur: '2.2s' },
        ].map((p, i) => (
            <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill={p.color}
                style={{ animation: `data-pulse ${p.dur} ease-in-out ${p.delay} infinite` }} />
        ))}
    </svg>
);

// ═══════════════════════════════════════════════════════════════════
//  SLOT WRAPPERS
// ═══════════════════════════════════════════════════════════════════

const CloudImageSlot = ({ which }) => (
    <div className="relative flex items-center justify-center h-full rounded-2xl overflow-hidden"
        style={{
            minHeight: '110px',
            background: 'radial-gradient(ellipse at center, rgba(71,85,105,0.14) 0%, transparent 80%)'
        }}>
        {which % 2 === 0 ? <CloudIllustration1 /> : <CloudIllustration2 />}
    </div>
);

const NasImageSlot = ({ which }) => (
    <div className="relative flex items-center justify-center h-full rounded-2xl overflow-hidden"
        style={{
            minHeight: '110px',
            background: 'radial-gradient(ellipse at center, rgba(109,40,217,0.14) 0%, transparent 80%)'
        }}>
        {which % 2 === 0 ? <NasIllustration1 /> : <NasIllustration2 />}
    </div>
);

// ═══════════════════════════════════════════════════════════════════
//  DATA CELL
// ═══════════════════════════════════════════════════════════════════

const DataCell = ({ feature, value, variant, delay = 0 }) => (
    <div
        className={`rounded-xl p-3 sm:p-4 h-full flex flex-col justify-center border ${variant === 'nas'
                ? 'bg-slate-900/50 border-purple-500/10'
                : 'bg-slate-900/40 border-slate-500/10'
            }`}
        style={{ animation: `slide-up 0.5s cubic-bezier(0.22,1,0.36,1) ${delay}ms both` }}
    >
        <div className="text-xs text-gray-300 mb-1 uppercase tracking-wide">{feature}</div>
        <div className={`text-sm sm:text-base font-semibold leading-snug ${variant === 'nas'
                ? 'text-slate-300'
                : 'text-slate-300'
            }`}>{value}</div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════
//  GROUP BUILDER
// ═══════════════════════════════════════════════════════════════════

function buildGroups(points, imageRight0) {
    const groups = [];
    let imgCount = 0;
    for (let i = 0; i < points.length; i += 2) {
        groups.push({
            points: points.slice(i, i + 2),
            imageRight: imgCount % 2 === 0 ? imageRight0 : !imageRight0,
            imgIndex: imgCount,
        });
        imgCount++;
    }
    return groups;
}

// ═══════════════════════════════════════════════════════════════════
//  SAVINGS BADGE (outside card)
// ═══════════════════════════════════════════════════════════════════

const SavingsBadge = () => (
    <div
        className="flex items-center justify-center mt-12"
        style={{ animation: 'slide-up 0.7s cubic-bezier(0.22,1,0.36,1) 400ms both' }}
    >
        <div
            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl border border-green-500/30 backdrop-blur-sm"
            style={{
                background: 'linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(16,185,129,0.12) 100%)',
                boxShadow: '0 0 24px rgba(34,197,94,0.15)',
                animation: 'pulse-glow 3s ease-in-out infinite',
            }}
        >
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(34,197,94,0.2)' }}>
                <TrendingUp style={{ width: 20, height: 20, color: '#4ade80' }} />
            </div>
            <div>
                <div className="text-sm text-green-500/90 uppercase tracking-wider font-medium">Savings with Synology NAS</div>
                <div className="text-green-400 font-bold text-lg leading-tight mt-2">Save $29,000 over 5 years</div>
            </div>
        </div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

const ComparisonTable = () => {
    useEffect(() => { injectStyles(); }, []);

    const comparisonPoints = [
        { feature: 'Monthly subscription', cloud: 'Per user basis', nas: '$0' },
        { feature: '5-year cost (50 users)', cloud: '$37,500', nas: '$8,500' },
        { feature: 'Storage limits', cloud: '1TB per user', nas: 'Unlimited' },
        { feature: 'Data sovereignty', cloud: 'Third-party servers', nas: 'Your infrastructure' },
        { feature: 'Customization', cloud: 'Limited', nas: 'Full control' },
    ];

    const nasGroups = buildGroups(comparisonPoints, true);
    const cloudGroups = buildGroups(comparisonPoints, false);

    const renderGroup = (group, variant, key) => {
        const ImageSlot = variant === 'nas'
            ? <NasImageSlot which={group.imgIndex} />
            : <CloudImageSlot which={group.imgIndex} />;

        return (
            <div key={key} className="grid grid-cols-3 gap-2 sm:gap-3 items-stretch">
                {group.imageRight ? (
                    <>
                        <div className="col-span-2 flex flex-col gap-2 sm:gap-3">
                            {group.points.map((p, pi) => (
                                <DataCell key={pi} feature={p.feature}
                                    value={variant === 'nas' ? p.nas : p.cloud}
                                    variant={variant} delay={key * 100 + pi * 60} />
                            ))}
                        </div>
                        <div className="col-span-1">{ImageSlot}</div>
                    </>
                ) : (
                    <>
                        <div className="col-span-1">{ImageSlot}</div>
                        <div className="col-span-2 flex flex-col gap-2 sm:gap-3">
                            {group.points.map((p, pi) => (
                                <DataCell key={pi} feature={p.feature}
                                    value={variant === 'nas' ? p.nas : p.cloud}
                                    variant={variant} delay={key * 100 + pi * 60} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div style={{ padding: '2rem 1rem 6rem', maxWidth: '100%' }}>
            {/* Header */}
            <div className="text-center mb-10 sm:mb-16" style={{ animation: 'fade-in 0.7s ease both' }}>
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">
                    Cloud vs. On-Premise
                </h3>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                    Choose the solution that fits your business needs
                </p>
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto items-start">

                {/* Cloud Storage Card */}
                <div
                    className="rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur-sm border border-slate-500/20"
                    style={{
                        background: 'linear-gradient(135deg, rgba(71,85,105,0.1) 0%, rgba(100,116,139,0.1) 100%)',
                        animation: 'slide-up 0.6s cubic-bezier(0.22,1,0.36,1) 100ms both',
                    }}
                >
                    <div className="flex items-center gap-3 mb-5 sm:mb-6">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'rgba(71,85,105,0.2)' }}>
                            <Cloud style={{ width: 22, height: 22, color: 'white' }} />
                        </div>
                        <h4 className="text-xl sm:text-2xl font-bold text-white">Cloud Storage</h4>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                        {cloudGroups.map((g, i) => renderGroup(g, 'cloud', i))}
                    </div>
                </div>

                {/* Synology NAS Card */}
                <div
                    className="rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur-sm border border-purple-500/20 relative overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, rgba(109,40,217,0.1) 0%, rgba(59,130,246,0.1) 100%)',
                        animation: 'slide-up 0.6s cubic-bezier(0.22,1,0.36,1) 200ms both',
                    }}
                >
                    <div className="absolute top-3 right-4 z-10">
                        <span className="text-white text-sm font-bold px-4 py-2 rounded-full"
                            style={{
                                background: 'linear-gradient(90deg, #7c3aed, #2563eb)',
                                boxShadow: '0 2px 12px rgba(124,58,237,0.4)',
                            }}>
                            RECOMMENDED
                        </span>
                    </div>
                    <div className="flex items-center gap-3 mb-5 mt-6 md:mt-0 sm:mb-6">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(59,130,246,0.2))' }}>
                            <Server style={{ width: 22, height: 22, color: '#a78bfa' }} />
                        </div>
                        <h4 className="text-xl sm:text-2xl font-bold text-white">Synology NAS</h4>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                        {nasGroups.map((g, i) => renderGroup(g, 'nas', i))}
                    </div>
                </div>
            </div>

            {/* Savings badge — outside both cards, centered */}
            <SavingsBadge />
        </div>
    );
};

export default ComparisonTable;